import * as React from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuth } from "firebase/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong,
  faAlignLeft,
  faHighlighter,
  faFilm,
  faBookOpenReader,
  faLightbulb,
  faBook,
  faTimes,
  faBars,
  faHeadphonesSimple,
  faImage,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import Topbar from "../general/topbar";
import GlobalStateContext from "./globalState";
import Translate from '../general/translate';
import LanguageSwitcher from "./languageSwitcher";

const NavBar = () => {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isFeaturesMegaMenuOpen, setIsFeaturesMegaMenuOpen] = React.useState(false);
  const [isResourcesMegaMenuOpen, setIsResourcesMegaMenuOpen] = React.useState(false);

  const { language, setLanguage } = React.useContext(GlobalStateContext);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam) {
      setLanguage(langParam);
    }
  }, [setLanguage]);

  React.useEffect(() => {
    console.log("Language changed to:", language);
  }, [language]);

  const getLocalizedLink = (path) => {

    if(path == "/blog/") {

      return language === 'en' ? path : `${path}${language}`;

    }

    return language === 'en' ? path : `/${language}${path}`;
  };

  const getAuthElement = () => (
    <button
      type="button"
      onClick={() => (location.href = user ? "/squeezed" : "/login")}
      className="transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300 text-[#27004F] bg-[#FFCA1D] font-bold rounded-lg text-sm px-4 py-2 text-center"
    >
      <Translate id={user ? "navbar.dashboard" : "navbar.login"} foldername="general" filename="navbar" />
    </button>
  );

  const toggleDropdownMenu = (menuType) => {
    if (menuType === 'features') {
      setIsFeaturesMegaMenuOpen(!isFeaturesMegaMenuOpen);
      setIsResourcesMegaMenuOpen(false);
    } else if (menuType === 'resources') {
      setIsResourcesMegaMenuOpen(!isResourcesMegaMenuOpen);
      setIsFeaturesMegaMenuOpen(false);
    }
    toggleBodyScroll(!isMenuOpen, !isFeaturesMegaMenuOpen, !isResourcesMegaMenuOpen);
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsFeaturesMegaMenuOpen(false);
    setIsResourcesMegaMenuOpen(false);
  };

  const toggleBodyScroll = (menuOpen, featuresOpen, resourcesOpen) => {
    if (window.innerWidth < 768) {
      document.body.style.overflow = (featuresOpen || resourcesOpen) ? "hidden" : "auto";
    }
  };

  React.useEffect(() => {
    if (!isMenuOpen && !isFeaturesMegaMenuOpen && !isResourcesMegaMenuOpen) {
      document.body.style.overflow = "auto";
    }
  }, [isMenuOpen, isFeaturesMegaMenuOpen, isResourcesMegaMenuOpen]);

  return (
    <div>
      <nav className="bg-[#27004F] border-white border-opacity-20 border-b fixed w-full z-20 top-0">
        <Topbar />
        <div className="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl p-4">
          <a href={"/"} className="flex items-center space-x-3 rtl:space-x-reverse">
            <img src="/logos/podsqueezelogo.png" className="h-6 sm:h-7" alt="Podsqueeze Logo" />
          </a>
          <div className="flex md:hidden gap-2">
            {getAuthElement()}
            <button
              onClick={toggleMobileMenu}
              type="button"
              className="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
              aria-controls="mega-menu-full"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only"><Translate id="navbar.openMenu" foldername="general" filename="navbar" /></span>
              <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} className="w-5 h-5" />
            </button>
          </div>
          <div
            id="mega-menu-full"
            className={`items-center justify-between font-medium ${isMenuOpen ? "block" : "hidden"} w-full md:flex md:w-auto md:order-1`}
          >
            <ul className="flex flex-col p-4 mt-4 rounded-lg md:space-x-8 rtl:space-x-reverse md:flex-row md:mt-0 md:border-0 md:p-0">
              <li className="relative group">
                <button
                  type="button"
                  onClick={() => toggleDropdownMenu('features')}
                  className="flex items-center justify-between w-full py-2 px-3 text-white text-opacity-80 rounded md:w-auto md:hover:bg-transparent md:border-0 md:hover:text-white md:p-0"
                >
                  <Translate id="navbar.features" foldername="general" filename="navbar" />
                  <svg
                    className="w-2.5 h-2.5 ms-2.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>
              </li>
              <li className="relative group">
                <button
                  type="button"
                  onClick={() => toggleDropdownMenu('resources')}
                  className="flex items-center justify-between w-full py-2 px-3 text-white text-opacity-80 rounded md:w-auto md:hover:bg-transparent md:border-0 md:hover:text-white md:p-0"
                >
                  <Translate id="navbar.resources" foldername="general" filename="navbar" />
                  <svg
                    className="w-2.5 h-2.5 ms-2.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>
              </li>
              <li>
                <a
                  href={"/pricing/"}
                  className="flex items-center justify-between w-full py-2 px-3 text-white text-opacity-80 rounded md:w-auto md:hover:bg-transparent md:border-0 md:hover:text-white md:p-0"
                >
                  <Translate id="navbar.pricing" foldername="general" filename="navbar" />
                </a>
              </li>
              <li>
                <a
                  href={"/#contact"}
                  className="flex items-center justify-between w-full py-2 px-3 text-white text-opacity-80 rounded md:w-auto hover:bg-zinc-100 md:hover:bg-transparent md:border-0 md:hover:text-white md:p-0"
                >
                  <Translate id="navbar.contact" foldername="general" filename="navbar" />
                </a>
              </li>
              <li className="block md:hidden">
                <LanguageSwitcher /> {/* Use LanguageSwitcher for mobile */}
              </li>
            </ul>
          </div>
          <div className="hidden md:flex md:items-center md:order-2 gap-2">
            {getAuthElement()}
            <div className="ml-auto flex items-center">
              <LanguageSwitcher /> {/* Use LanguageSwitcher for desktop */}
            </div>
          </div>
        </div>
        {/* Mega Menus */}
        {isFeaturesMegaMenuOpen && (
          <div
            id="mega-menu-features-dropdown"
            className="absolute left-0 right-0 w-full border-gray-200 shadow-xl bg-[#F2F0F8] border-y z-20 h-auto overflow-y-auto sm:overflow-y-visible"
            onMouseLeave={() => setIsFeaturesMegaMenuOpen(false)}
            style={{ maxHeight: window.innerWidth >= 768 ? '100vh' : 'calc(100vh - 280px)' }}
          >
            <div className="grid max-w-screen-xl px-4 py-5 mx-auto text-gray-900 sm:grid-cols-2 md:px-6">
              <ul className="sm:border-r-2 sm:p-6">
                <li className="block py-3 px-3 rounded-lg hover:bg-[#E8E5F1] mb-2">
                  <a href={getLocalizedLink("/repurpose-podcast-content/")} target="_blank" rel="noopener noreferrer">
                    <div className="font-bold text-[#27004F]">
                      <Translate id="navbar.seeAllFeatures" foldername="general" filename="navbar" />
                      <FontAwesomeIcon icon={faArrowRightLong} className="ml-2" />
                    </div>
                  </a>
                </li>
                <li className="border-t-2 pt-4">
                  <div className="font-bold text-xs text-[#27004F] opacity-40 mb-2">
                    <Translate id="navbar.mainFeatures" foldername="general" filename="navbar" />
                  </div>
                  <ul>
                    <li>
                      <a
                        href={getLocalizedLink("/ai-podcast-transcript-generator/")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                      >
                        <div className="font-bold text-[#27004F] mb-2">
                          <FontAwesomeIcon icon={faAlignLeft} className="mr-2 text-[#27004F]" />
                          <Translate id="navbar.podcastTranscription" foldername="general" filename="navbar" />
                        </div>
                        <span className="text-sm text-gray-500">
                          <Translate id="navbar.transcriptionDescription" foldername="general" filename="navbar" />
                        </span>
                      </a>
                    </li>
                    <li>
                      <a
                        href={getLocalizedLink("/podcast-summary-generator/")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                      >
                        <div className="font-bold text-[#27004F] mb-2">
                          <FontAwesomeIcon icon={faHighlighter} className="text-md mr-2 text-[#27004F]" />
                          <Translate id="navbar.podcastSummarizer" foldername="general" filename="navbar" />
                        </div>
                        <span className="text-sm text-gray-500">
                          <Translate id="navbar.summarizerDescription" foldername="general" filename="navbar" />
                        </span>
                      </a>
                    </li>
                    <li>
                      <a
                        href={getLocalizedLink("/podcast-clip-maker/")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                      >
                        <div className="font-bold text-[#27004F] mb-2">
                          <FontAwesomeIcon icon={faFilm} className="mr-2 text-[#27004F]" />
                          <Translate id="navbar.podcastClipMaker" foldername="general" filename="navbar" />
                        </div>
                        <span className="text-sm text-gray-500">
                          <Translate id="navbar.clipMakerDescription" foldername="general" filename="navbar" />
                        </span>
                      </a>
                    </li>
                    <li>
                      <a
                        href={getLocalizedLink("/ai-audio-enhancer/")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                      >
                        <div className="font-bold text-[#27004F] mb-2">
                          <FontAwesomeIcon icon={faHeadphonesSimple} className="mr-2 text-[#27004F]" />
                          <Translate id="navbar.audioEnhancer" foldername="general" filename="navbar" />
                        </div>
                        <span className="text-sm text-gray-500">
                          <Translate id="navbar.audioEnhancerDescription" foldername="general" filename="navbar" />
                        </span>
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
              <div className="flex flex-col items-center w-5/6 mx-auto my-12 sm:m-auto">
                <h2 className="font-bold text-xs text-[#27004F] opacity-40 mb-4">
                  <Translate id="navbar.podsqueezeFeaturesWalkthrough" foldername="general" filename="navbar" />
                </h2>
                <a href="https://www.youtube.com/watch?v=8o3sDPNbOTk" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/home/podsqueeze-walkthrough.webp"
                    alt="Lessons From A Podcaster"
                    className="rounded-lg w-auto sm:w-3/4 m-auto"
                  />
                </a>
              </div>
            </div>
          </div>
        )}
        {isResourcesMegaMenuOpen && (
          <div
            id="mega-menu-resources-dropdown"
            className="absolute left-0 right-0 w-full border-gray-200 shadow-xl bg-[#F2F0F8] border-y z-20 overflow-y-auto"
            onMouseLeave={() => setIsResourcesMegaMenuOpen(false)}
            style={{ maxHeight: "calc(100vh - 330px)" }}
          >
            <div className="grid max-w-screen-xl px-4 py-5 mx-auto text-gray-900 sm:grid-cols-2 md:px-6">
              <ul className="sm:border-r-2 sm:p-6">
                <li>
                  <a
                    href={getLocalizedLink("/blog/")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                  >
                    <div className="font-bold text-[#27004F] mb-2">
                      <FontAwesomeIcon icon={faBookOpenReader} className="mr-2" />
                      <Translate id="navbar.blog" foldername="general" filename="navbar" />
                    </div>
                    <span className="text-sm text-gray-500">
                      <Translate id="navbar.blogDescription" foldername="general" filename="navbar" />
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.topicsforpodcasts.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                  >
                    <div className="font-bold text-[#27004F] mb-2">
                      <FontAwesomeIcon icon={faLightbulb} className="mr-2" />
                      <Translate id="navbar.podcastTopicIdeas" foldername="general" filename="navbar" />
                    </div>
                    <span className="text-sm text-gray-500 leading-tight">
                      <Translate id="navbar.topicIdeasDescription" foldername="general" filename="navbar" />
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://podsqueeze.gumroad.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                  >
                    <div className="font-bold text-[#27004F] mb-2">
                      <FontAwesomeIcon icon={faBook} className="mr-2" />
                      <Translate id="navbar.actionableEbooks" foldername="general" filename="navbar" />
                    </div>
                    <span className="text-sm text-gray-500">
                      <Translate id="navbar.ebooksDescription" foldername="general" filename="navbar" />
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href={"/free-podcast-thumbnail-maker/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                  >
                    <div className="font-bold text-[#27004F] mb-2">
                      <FontAwesomeIcon icon={faImage} className="mr-2" />
                      <Translate id="navbar.freeThumbnailMaker" foldername="general" filename="navbar" />
                    </div>
                    <span className="text-sm text-gray-500">
                      <Translate id="navbar.freeThumbnailMakerDescription" foldername="general" filename="navbar" />
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href={"/podcast-seo-optimizer/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg hover:bg-[#E8E5F1]"
                  >
                    <div className="font-bold text-[#27004F] mb-2">
                      <FontAwesomeIcon icon={faSearch} className="mr-2" />
                      <Translate id="navbar.seoOptimizer" foldername="general" filename="navbar" />
                    </div>
                    <span className="text-sm text-gray-500">
                      <Translate id="navbar.seoOptimizerDescription" foldername="general" filename="navbar" />
                    </span>
                  </a>
                </li>
              </ul>
              <div className="flex flex-col items-center w-full sm:w-5/6 mx-auto my-12 sm:m-auto">
                <img
                  src="/logos/logo-lessons-from-a-podcaster.webp"
                  alt="Lessons From A Podcaster"
                  className="rounded-lg mb-4 h-24"
                />
                <p className="text-sm text-gray-500 mb-4 text-center">
                  <Translate id="navbar.podcastDescription" foldername="general" filename="navbar" />
                </p>
                <div className="flex space-x-2 w-full justify-between">
                  <a
                    href="https://open.spotify.com/show/75hfiON226nVpCXrd9Gsaa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center rounded-lg hover:bg-zinc-100"
                  >
                    <img src="/lessons-from-a-podcaster/listen-on-spotify.webp" alt="Button 1" className="w-auto" />
                  </a>
                  <a
                    href="https://podcasts.apple.com/gb/podcast/lessons-from-a-podcaster/id1724239437"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center rounded-lg hover:bg-zinc-100"
                  >
                    <img src="/lessons-from-a-podcaster/listen-on-apple-podcasts.webp" alt="Button 1" className="w-auto" />
                  </a>
                  <a
                    href="https://www.youtube.com/@Podsqueeze/sub_confirmation=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center rounded-lg hover:bg-zinc-100"
                  >
                    <img src="/lessons-from-a-podcaster/listen-on-youtube.webp" alt="Button 1" className="w-auto" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
      <div className="h-16 mb-24 sm:mb-20"></div>
    </div>
  );
};

export default NavBar;
